/**
 * NotificationBell — header bell with unread badge + a dropdown of recent
 * notifications. The full feed lives at /notifications (NotificationsPage); this
 * is the at-a-glance peek. Clicking an item marks it read and navigates to the
 * resolved target page.
 *
 * Unread count polls every 60s so reminders surface without a refresh.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { systemApi, type Notification } from '@/api';
import { resolveNotifLink, timeAgoTh, notifAccent } from '@/lib/notif';

const UNREAD_KEY = ['notifications', 'unread-count'];
const RECENT_KEY = ['notifications', 'recent'];

export function NotificationBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const { data: unread = 0 } = useQuery({
    queryKey: UNREAD_KEY,
    queryFn: () => systemApi.getUnreadCount().then((r) => r.count ?? 0),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const { data: recent = [], isLoading } = useQuery({
    queryKey: RECENT_KEY,
    queryFn: () => systemApi.listNotifications({ limit: 8 }),
    enabled: open,
    staleTime: 15_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => systemApi.markNotificationRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UNREAD_KEY });
      void qc.invalidateQueries({ queryKey: RECENT_KEY });
    },
  });

  const markAll = useMutation({
    mutationFn: () => systemApi.markAllNotificationsRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UNREAD_KEY });
      void qc.invalidateQueries({ queryKey: RECENT_KEY });
      void qc.invalidateQueries({ queryKey: ['notifications', 'list'] });
    },
  });

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function openItem(n: Notification) {
    if (!n.read_at) markRead.mutate(n.id);
    setOpen(false);
    navigate(resolveNotifLink(n.link));
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        title="การแจ้งเตือน"
        aria-label="การแจ้งเตือน"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center tabular-nums">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
            <span className="text-sm font-semibold">การแจ้งเตือน</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="flex items-center gap-1 text-xs text-brand-700 hover:text-brand-800 disabled:opacity-50"
              >
                <CheckCheck className="w-3.5 h-3.5" /> อ่านทั้งหมด
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {isLoading ? (
              <p className="px-4 py-8 text-sm text-gray-400 text-center">กำลังโหลด…</p>
            ) : recent.length === 0 ? (
              <p className="px-4 py-10 text-sm text-gray-400 text-center">ไม่มีการแจ้งเตือน</p>
            ) : (
              recent.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => openItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition flex gap-3 ${
                    n.read_at ? '' : 'bg-brand-50/40'
                  }`}
                >
                  <span
                    className="mt-1 w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: n.read_at ? '#d1d5db' : notifAccent(n.payload as Record<string, unknown>) }}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-800 truncate">{n.title}</span>
                    {n.body && <span className="block text-xs text-gray-500 line-clamp-2 mt-0.5">{n.body}</span>}
                    <span className="block text-[11px] text-gray-400 mt-1">{timeAgoTh(n.created_at)}</span>
                  </span>
                </button>
              ))
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
            className="w-full px-4 py-2.5 text-sm text-center text-brand-700 hover:bg-gray-50 border-t border-gray-100 font-medium"
          >
            ดูทั้งหมด
          </button>
        </div>
      )}
    </div>
  );
}
